// Login-free {CM} request for guests. Guests can't log in (they have no real account until they
// accept an invitation), so they request using their attendee rsvpToken — the same token the RSVP
// email/confirmation carries. They can see who's coming (RSVP'd Yes), pick specific people to meet,
// or just ask to be grouped ("no preference").
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import datepipeModel from '../../models/datepipemodel';
import { toast } from 'react-toastify';

interface Attendee { id: string; name: string; email: string; }

const CmGuestRequest: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!token) { setError('This link is not valid.'); setLoading(false); return; }
    ApiClient.get('api/cm/guest-request/info', { token }).then((res: any) => {
      if (res?.success && res.data) {
        setInfo(res.data);
        setSelectedIds(res.data.selectedAttendeeIds || []);
      } else {
        setError(res?.message || res?.error?.message || 'This link is invalid or has expired.');
      }
      setLoading(false);
    }).catch(() => { setError('Something went wrong. Please try again.'); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const label = info?.connectMeetLabel || 'ConnectMeet';
  const attendees: Attendee[] = info?.attendees || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter(a => (a.name || '').toLowerCase().indexOf(q) !== -1);
  }, [attendees, search]);

  const isSelected = (id: string) => selectedIds.includes(String(id));
  const toggle = (id: string) => {
    const key = String(id);
    setSelectedIds(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const submit = (attendeeIds: string[]) => {
    if (!token) return;
    setSubmitting(true);
    ApiClient.post('api/cm/guest-request', { token, attendeeIds }).then((res: any) => {
      setSubmitting(false);
      if (res?.success) { setDone(true); setDoneMsg(res.message || 'Request sent.'); toast.success(res.message || 'Request sent.'); }
      else toast.error(res?.message || res?.error?.message || 'Could not send your request.');
    }).catch(() => { setSubmitting(false); toast.error('Could not send your request.'); });
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Link not valid</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
          <div className="text-green-500 text-6xl mb-4">&#10003;</div>
          <h1 className="text-2xl font-bold mb-2">You're on the list!</h1>
          <p className="text-gray-600">{doneMsg || `Your request has been sent to the group leader.`}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-md max-w-lg w-full my-8">
        <h1 className="text-2xl font-bold mb-1 text-center">Request a {label}</h1>
        {info?.eventTitle && (
          <p className="text-center text-gray-700">
            <span className="font-semibold">{info.eventTitle}</span>
            {info?.date && <span className="block text-gray-500 text-sm mt-1">{datepipeModel.date(info.date)}</span>}
          </p>
        )}

        {/* Top selection bar: pick who you want to meet, then send. */}
        <div className="mt-4 border border-orange-200 bg-orange-50 rounded-lg p-3">
          <p className="text-sm text-gray-700 mb-2">
            {selectedIds.length
              ? `You want to meet ${selectedIds.length} ${selectedIds.length === 1 ? 'person' : 'people'}.`
              : `Pick who you'd like to meet below, or just ask to be grouped.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => submit(selectedIds)}
              disabled={submitting || selectedIds.length === 0}
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded"
            >
              {submitting ? 'Sending…' : `Send ${label} request (${selectedIds.length})`}
            </button>
            <button
              onClick={() => submit([])}
              disabled={submitting}
              className="flex-1 border border-orange-300 text-orange-700 hover:bg-orange-100 disabled:opacity-50 text-sm font-medium px-4 py-2.5 rounded"
            >
              No preference — just group me
            </button>
          </div>
        </div>

        {/* Who's coming list */}
        <div className="mt-5">
          <h2 className="font-semibold text-gray-800 mb-2">Who's invited</h2>
          {attendees.length === 0 ? (
            <p className="text-sm text-gray-500">No one else is invited yet. You can still ask to be grouped above.</p>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name…"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
                {filtered.length === 0 && <p className="text-sm text-gray-500">No matches.</p>}
                {filtered.map(a => {
                  const sel = isSelected(a.id);
                  return (
                    <div key={a.id} className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 ${sel ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}>
                      <span className="text-sm text-gray-800 capitalize truncate">{a.name}</span>
                      <button
                        onClick={() => toggle(a.id)}
                        className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded ${sel ? 'bg-orange-500 text-white' : 'border border-orange-400 text-orange-600 hover:bg-orange-50'}`}
                      >
                        {sel ? 'Selected ✓' : `Request ${label}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CmGuestRequest;
